/**
 * @fileoverview Style mapping utility for v2 schema-driven styling
 * @module screen-view-v2/utils/styleMapper
 *
 * Maps v2 schema styles to React CSSProperties for inline styling.
 * Base styles are defined in SCSS; inline styles from schema override them.
 *
 * Key v1 → v2 changes:
 * - Padding: flat paddingLeft/Right/Top/Bottom → EdgeInsets { start, end, top, bottom }
 * - Corner radius: mainCornerRadius + individuals → CornerRadius { uniform?, topStart?, ... }
 * - TextAlign: 'left'/'right' → 'start'/'end' (RTL-aware)
 */

import type { CSSProperties } from 'react';
import type {
  TypographyStyle,
  ImageStyle,
  ClipStyle,
  FeaturedMediaStyle,
  TapTargetStyle,
  FrameLayout,
  CanvasStyle,
  EdgeInsets,
  CornerRadius,
  TextAlignment,
  VerticalAlignment,
  HorizontalAlignment,
  OptionsListStyle,
  ProductCatalogStyle,
  ProductOfferStyle,
  PaywallFooterStyle,
  GradientOverlay,
  ShadowStyle,
  TextFieldConfig,
  TapTargetBackgroundImage,
} from '@segmently/schema-models/v2';

/**
 * Default font fallback chains for schema font families.
 * Maps schema fontFamily values to CSS font-family with fallbacks.
 * Ensures graceful degradation on non-Apple platforms.
 *
 * These are the hardcoded defaults used when no fontConfig is provided.
 * For configurable per-platform mappings, use buildFallbackChains()
 * from @modules/font-config/data/systemFontCatalog.
 *
 * Source of truth for CSS --sv2-font-* vars in variables.css (UI chrome only).
 */
export const DEFAULT_FONT_FALLBACK_CHAINS: Record<string, string> = {
  // Sans-serif
  'SF Pro': "'SF Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  'SF ProRounded': "'SF ProRounded', 'SF Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  'Helvetica Neue': "'Helvetica Neue', Helvetica, Arial, sans-serif",
  'Avenir Next': "'Avenir Next', 'Avenir', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  // Serif
  'New York': "'New York', 'Times New Roman', Georgia, serif",
  'Georgia': "'Georgia', 'Times New Roman', serif",
  'Baskerville': "'Baskerville', 'Palatino', 'Times New Roman', Georgia, serif",
  // Monospace
  'SF Mono': "'SF Mono', Consolas, 'Courier New', monospace",
};

/**
 * Filters out undefined values from an object.
 * Only includes properties that have defined values.
 */
function filterUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as Partial<T>;
}

// ============= Shared Helpers =============

/**
 * Maps EdgeInsets to CSS padding properties.
 *
 * V2 uses RTL-aware naming: start/end instead of left/right.
 * For web (LTR context): start → paddingLeft, end → paddingRight.
 *
 * @param insets - EdgeInsets from v2 schema (optional)
 * @returns CSSProperties with padding values
 */
export function mapEdgeInsetsToCSS(insets?: EdgeInsets): CSSProperties {
  if (!insets) {
    return {};
  }

  return filterUndefined({
    paddingLeft: insets.start,
    paddingRight: insets.end,
    paddingTop: insets.top,
    paddingBottom: insets.bottom,
  }) as CSSProperties;
}

/**
 * Maps CornerRadius to CSS border-radius properties.
 *
 * V2 CornerRadius has `uniform` (shorthand for all corners) and individual
 * corners (topStart, topEnd, bottomStart, bottomEnd). Individual values
 * override uniform when both are present.
 *
 * @param radius - CornerRadius from v2 schema (optional)
 * @returns CSSProperties with border-radius values
 */
export function mapCornerRadiusToCSS(radius?: CornerRadius): CSSProperties {
  if (!radius) {
    return {};
  }

  const styles: Record<string, unknown> = {};

  // Uniform applies to all corners
  if (radius.uniform !== undefined) {
    styles.borderRadius = radius.uniform === null ? 0 : radius.uniform;
  }

  // Individual corners override uniform
  if (radius.topStart !== undefined) {
    styles.borderTopLeftRadius = radius.topStart === null ? 0 : radius.topStart;
  }
  if (radius.topEnd !== undefined) {
    styles.borderTopRightRadius = radius.topEnd === null ? 0 : radius.topEnd;
  }
  if (radius.bottomStart !== undefined) {
    styles.borderBottomLeftRadius = radius.bottomStart === null ? 0 : radius.bottomStart;
  }
  if (radius.bottomEnd !== undefined) {
    styles.borderBottomRightRadius = radius.bottomEnd === null ? 0 : radius.bottomEnd;
  }

  return styles as CSSProperties;
}

/**
 * Maps v2 TextAlignment to CSS textAlign.
 *
 * V2 uses RTL-aware values: 'start' and 'end' instead of 'left' and 'right'.
 * For web (LTR context): start → left, end → right.
 *
 * @param align - TextAlignment from v2 schema (optional)
 * @returns CSS textAlign value
 */
export function mapTextAlignmentToCSS(align?: TextAlignment): CSSProperties['textAlign'] {
  if (!align) {
    return undefined;
  }

  switch (align) {
    case 'start':
      return 'left';
    case 'end':
      return 'right';
    case 'center':
      return 'center';
    default:
      return undefined;
  }
}

/**
 * Maps VerticalAlignment to CSS justify-content.
 *
 * V2 uses 'top'/'center'/'bottom'; maps to flex alignment values.
 * Default is 'center' when no alignment is specified.
 *
 * @param alignment - VerticalAlignment from v2 schema (optional)
 * @returns CSS justifyContent value
 */
export const mapVerticalAlignment = (
  alignment?: VerticalAlignment
): CSSProperties['justifyContent'] => {
  switch (alignment) {
    case 'top': return 'flex-start';
    case 'bottom': return 'flex-end';
    case 'center':
    default: return 'center';
  }
};

/**
 * Maps HorizontalAlignment to CSS align-items.
 *
 * V2 uses RTL-aware 'start'/'center'/'end'; maps to flex alignment values.
 *
 * @param alignment - HorizontalAlignment from v2 schema (optional)
 * @returns CSS alignItems value, or undefined if no alignment set
 */
export const mapHorizontalAlignment = (
  alignment?: HorizontalAlignment
): CSSProperties['alignItems'] | undefined => {
  switch (alignment) {
    case 'start': return 'flex-start';
    case 'center': return 'center';
    case 'end': return 'flex-end';
    default: return undefined;
  }
};

/**
 * Maps SectionSelfAlignment (v2.19.0) to CSS align-self for width-constrained sections.
 *
 * @param alignment - SectionSelfAlignment from v2 schema (optional)
 * @returns CSS alignSelf value, or undefined if no alignment set
 */
export const mapSectionSelfAlignment = (
  alignment?: string
): CSSProperties['alignSelf'] | undefined => {
  switch (alignment) {
    case 'start': return 'flex-start';
    case 'center': return 'center';
    case 'end': return 'flex-end';
    case 'stretch': return 'stretch';
    default: return undefined;
  }
};

// ============= Style Mappers =============

/**
 * Maps TypographyStyle to CSS properties.
 *
 * Handles: align, fontFamily, lineHeight, fontSize, fontWeight, color,
 * backgroundColor, backgroundColorOpacity, backgroundCornerRadius, insets (padding).
 *
 * @param style - TypographyStyle from v2 schema (optional)
 * @returns CSSProperties for inline styling
 */
export function mapTypographyStyles(
  style?: TypographyStyle,
  fontOverrides?: Record<string, string>,
): CSSProperties {
  if (!style) {
    return {};
  }

  // Apply fallback chain if fontFamily matches a known schema font.
  // fontOverrides (from FontConfigContext) take priority over defaults.
  const chains = fontOverrides ?? DEFAULT_FONT_FALLBACK_CHAINS;
  const fontFamily = style.fontFamily
    ? (chains[style.fontFamily] || DEFAULT_FONT_FALLBACK_CHAINS[style.fontFamily] || style.fontFamily)
    : undefined;

  // textDecorationColor is an editor-extended field (not in TypographyStyle schema)
  // When present, it means "enable line-through with this color"
  const textDecorationColor = (style as Record<string, unknown>).textDecorationColor as string | undefined;

  // Apply opacity to backgroundColor if backgroundColorOpacity is set
  let backgroundColor = style.backgroundColor;
  if (backgroundColor && style.backgroundColorOpacity !== undefined && style.backgroundColorOpacity < 1) {
    backgroundColor = hexToRgba(backgroundColor, style.backgroundColorOpacity);
  }

  const styles: CSSProperties = filterUndefined({
    fontFamily,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    color: style.color,
    textAlign: mapTextAlignmentToCSS(style.align),
    lineHeight: style.lineHeight !== undefined ? `${style.lineHeight}px` : undefined,
    backgroundColor,
    borderRadius: style.backgroundCornerRadius,
    textDecoration: textDecorationColor ? 'line-through' : undefined,
    textDecorationColor: textDecorationColor || undefined,
    ...mapEdgeInsetsToCSS(style.insets),
  });

  return styles;
}

/**
 * Maps ImageStyle to CSS properties.
 *
 * Handles: dimensions (width, height, heightPercentage),
 * cornerRadii (CornerRadius).
 *
 * @param style - ImageStyle from v2 schema (optional)
 * @returns CSSProperties for inline styling
 */
export function mapImageStyles(style?: ImageStyle): CSSProperties {
  if (!style) {
    return {};
  }

  const styles: Record<string, unknown> = {};

  // Size from dimensions
  if (style.dimensions) {
    if (style.dimensions.width !== undefined) {
      styles.width = style.dimensions.width;
    }

    // Height - prefer heightPercentage, fall back to height
    if (style.dimensions.heightPercentage !== undefined) {
      styles.height = `${style.dimensions.heightPercentage}%`;
    } else if (style.dimensions.height !== undefined) {
      styles.height = style.dimensions.height;
    }
  }

  // Corner radius from cornerRadii
  Object.assign(styles, mapCornerRadiusToCSS(style.cornerRadii));

  return styles as CSSProperties;
}

/**
 * Maps ClipStyle to CSS properties.
 *
 * Handles: dimensions (width, height, heightPercentage) via SizeSpec,
 * cornerRadii (CornerRadius). Mirrors mapImageStyles pattern.
 *
 * @param style - ClipStyle from v2 schema (optional)
 * @returns CSSProperties for inline styling
 */
export function mapClipStyles(style?: ClipStyle): CSSProperties {
  if (!style) {
    return {};
  }

  const styles: Record<string, unknown> = {};

  // Size from dimensions (SizeSpec)
  if (style.dimensions) {
    if (style.dimensions.width !== undefined) {
      styles.width = style.dimensions.width;
    }

    if (style.dimensions.heightPercentage !== undefined) {
      styles.height = `${style.dimensions.heightPercentage}%`;
    } else if (style.dimensions.height !== undefined) {
      styles.height = style.dimensions.height;
    }
  }

  // Corner radius from cornerRadii
  Object.assign(styles, mapCornerRadiusToCSS(style.cornerRadii));

  return styles as CSSProperties;
}

/**
 * Maps FeaturedMediaStyle to CSS properties.
 *
 * Handles: dimensions as maxHeight/minHeight (heightPercentage),
 * cornerRadii (CornerRadius).
 *
 * @param style - FeaturedMediaStyle from v2 schema (optional)
 * @returns CSSProperties for inline styling
 */
export function mapFeaturedMediaStyles(style?: FeaturedMediaStyle): CSSProperties {
  if (!style) {
    return {};
  }

  const styles: Record<string, unknown> = {};

  // Dimensions — heightPercentage maps to maxHeight/minHeight
  if (style.dimensions) {
    if (style.dimensions.heightPercentage !== undefined) {
      styles.maxHeight = `${style.dimensions.heightPercentage}%`;
      styles.minHeight = `${style.dimensions.heightPercentage}%`;
    }
  }

  // Corner radius from cornerRadii
  Object.assign(styles, mapCornerRadiusToCSS(style.cornerRadii));

  return styles as CSSProperties;
}

/**
 * Maps ShadowStyle to CSS box-shadow property.
 */
export function mapShadowStyleToCSS(shadow?: ShadowStyle | null): CSSProperties {
  if (!shadow) return {};
  const { color = '#00000040', offsetX = 0, offsetY = 2, blur = 4, spread = 0 } = shadow;
  return { boxShadow: `${offsetX}px ${offsetY}px ${blur}px ${spread}px ${color}` };
}

const SCALE_MODE_TO_CSS: Record<string, string> = {
  scaleAspectFill: 'cover',
  scaleAspectFit: 'contain',
  scaleToFill: '100% 100%',
  center: 'auto',
  top: 'auto',
  bottom: 'auto',
  left: 'auto',
  right: 'auto',
};

/**
 * Maps TapTargetBackgroundImage to CSS background properties.
 */
export function mapTapTargetBackgroundImageToCSS(bgImage?: TapTargetBackgroundImage | null): CSSProperties {
  if (!bgImage?.asset) return {};
  const url = bgImage.asset.original ?? bgImage.asset.small;
  if (!url) return {};
  const size = SCALE_MODE_TO_CSS[bgImage.scaleMode as string] ?? 'cover';
  return {
    backgroundImage: `url(${url})`,
    backgroundSize: size,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
}

/**
 * Maps TapTargetStyle to CSS properties.
 *
 * Handles: fullWidth, backgroundColor, gradient, backgroundImage, shadow, opacity,
 * dimensions (width, height), borders (color, width, radius), insets (padding).
 *
 * @param style - TapTargetStyle from v2 schema (optional)
 * @returns CSSProperties for inline styling
 */
export function mapTapTargetStyles(style?: TapTargetStyle): CSSProperties {
  if (!style) {
    return {};
  }

  const styles: Record<string, unknown> = {};

  // Background color (base layer)
  if (style.backgroundColor !== undefined) {
    styles.backgroundColor = style.backgroundColor;
  }

  // Gradient background (layered above backgroundColor)
  if (style.gradient) {
    const gradientCSS = mapGradientOverlayToCSS(style.gradient);
    if (gradientCSS) {
      Object.assign(styles, gradientCSS);
    }
  }

  // Background image (layered above gradient)
  Object.assign(styles, mapTapTargetBackgroundImageToCSS(style.backgroundImage));

  // Box shadow
  Object.assign(styles, mapShadowStyleToCSS(style.shadow));

  // Opacity
  if (style.opacity !== undefined && style.opacity !== 1) {
    styles.opacity = style.opacity;
  }

  // Border styles from borders object
  if (style.borders) {
    // Border radius from borders.radius
    Object.assign(styles, mapCornerRadiusToCSS(style.borders.radius));

    // Border gradient fallback: use first gradient color as solid border color
    // Normalize: editor path-setter may create object instead of array
    const borderGradientColors = style.borders.gradient?.colors;
    const normalizedBorderColors = borderGradientColors
      ? (Array.isArray(borderGradientColors) ? borderGradientColors : Object.values(borderGradientColors) as string[])
      : [];
    if (normalizedBorderColors.length) {
      styles.borderColor = normalizedBorderColors[0];
    } else if (style.borders.color !== undefined) {
      styles.borderColor = style.borders.color;
    }
    if (style.borders.width !== undefined) {
      styles.borderWidth = style.borders.width;
    }
  }

  // Padding from insets
  Object.assign(styles, mapEdgeInsetsToCSS(style.insets));

  // Width - fullWidth overrides explicit width
  if (style.fullWidth === true || (style.fullWidth as unknown) === 'true') {
    styles.width = '100%';
  } else if (style.dimensions?.width !== undefined) {
    styles.width = style.dimensions.width;
  }

  // Height from dimensions
  if (style.dimensions?.height !== undefined) {
    styles.height = style.dimensions.height;
  }

  return styles as CSSProperties;
}

/**
 * Maps FrameLayout to CSS properties.
 *
 * Handles: insets (padding).
 *
 * @param layout - FrameLayout from v2 schema (optional)
 * @returns CSSProperties for inline styling
 */
export function mapFrameLayoutStyles(layout?: FrameLayout): CSSProperties {
  if (!layout) {
    return {};
  }

  return mapEdgeInsetsToCSS(layout.insets);
}

/**
 * Maps CanvasStyle to CSS properties.
 *
 * Handles: insets (padding).
 * Note: background is handled separately by Background component.
 *
 * @param canvas - CanvasStyle from v2 schema (optional)
 * @returns CSSProperties for inline styling
 */
export function mapCanvasStyles(canvas?: CanvasStyle): CSSProperties {
  if (!canvas) {
    return {};
  }

  return mapEdgeInsetsToCSS(canvas.insets);
}

/** Shared native/custom input styling; placeholder typography does not replace entered-text color. */
export function mapTextFieldInputStyles(field?: Partial<Pick<TextFieldConfig, 'appearance' | 'placeholder'>>, fontOverrides?: Record<string, string>): CSSProperties {
  const appearance = field?.appearance;
  const typography = mapTypographyStyles(field?.placeholder?.appearance, fontOverrides);
  return {
    ...(appearance?.backgroundColor && { backgroundColor: appearance.backgroundColor }),
    ...(appearance?.borderColor && { borderColor: appearance.borderColor }),
    ...(appearance?.enteredTextColor && { color: appearance.enteredTextColor, '--field-text-color': appearance.enteredTextColor }),
    ...(typography.color && { '--field-placeholder-color': typography.color }),
    ...(typography.fontFamily && { fontFamily: typography.fontFamily }),
    ...(typography.fontSize && { fontSize: typography.fontSize }),
    ...(typography.fontWeight && { fontWeight: typography.fontWeight }),
    ...(typography.lineHeight && { lineHeight: typography.lineHeight }),
    ...(typography.textAlign && { textAlign: typography.textAlign }),
  } as CSSProperties;
}

/**
 * Maps OptionsListStyle to CSS properties.
 *
 * Handles: backgroundColor, gradient (backgroundImage), borders (color, width, radius), insets (padding).
 *
 * @param style - OptionsListStyle from v2 schema (optional)
 * @returns CSSProperties for inline styling
 */
export function mapOptionsListStyleToCSS(style?: OptionsListStyle): CSSProperties {
  if (!style) {
    return {};
  }

  const gradientCSS = style.gradient ? mapGradientOverlayToCSS(style.gradient) : undefined;
  const borderWidthStyles = style.borders?.width === null
    ? { borderWidth: 0, borderStyle: 'none' as const }
    : style.borders?.width !== undefined
      ? { borderWidth: style.borders.width, borderStyle: 'solid' as const }
      : {};

  return filterUndefined({
    backgroundColor: style.backgroundColor,
    ...gradientCSS,
    ...(style.borders?.color && { borderColor: style.borders.color }),
    ...borderWidthStyles,
    ...mapCornerRadiusToCSS(style.borders?.radius),
    ...mapEdgeInsetsToCSS(style.insets),
  }) as CSSProperties;
}

// ============= Gradient Utilities =============

/**
 * Converts a hex color string to an rgba() CSS string.
 *
 * @param hex - Hex color (e.g. "#FF0000" or "#F00")
 * @param alpha - Alpha channel, 0–1
 * @returns rgba CSS string
 */
export function hexToRgba(hex: string, alpha: number): string {
  const cleanHex = hex.replace('#', '');
  const fullHex = cleanHex.length === 3
    ? cleanHex.split('').map(c => c + c).join('')
    : cleanHex;
  const r = parseInt(fullHex.slice(0, 2), 16);
  const g = parseInt(fullHex.slice(2, 4), 16);
  const b = parseInt(fullHex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Maps a GradientOverlay schema object to CSS properties.
 *
 * Uses `backgroundImage` (not `background` shorthand) to preserve `backgroundColor`
 * as a fallback layer underneath the gradient.
 *
 * Supports:
 * - New format: `stops[]` array with per-stop color + position (takes priority)
 * - Legacy format: `colors[]` + `locations[]` arrays
 * - `angle` field (CSS degrees, default: 0 for bottom-to-top)
 * - `size` field (background-size percentage)
 * - Single color: solid at bottom, fading to transparent at `heightPercentage`
 *
 * @param gradient - GradientOverlay from v2 schema
 * @returns CSSProperties with backgroundImage gradient, or undefined if invalid
 */
export function mapGradientOverlayToCSS(gradient: GradientOverlay | undefined): CSSProperties | undefined {
  if (!gradient) return undefined;

  // --- Resolve color stops (stops[] takes priority over colors[]) ---
  let colorStops: string | undefined;
  let angle: number;

  // Normalize stops[] if present
  const stops = gradient.stops && Array.isArray(gradient.stops) && gradient.stops.length > 0
    ? gradient.stops
    : undefined;

  if (stops) {
    // New format: structured stops with position
    const sorted = [...stops].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    colorStops = sorted
      .map(s => `${s.color} ${Math.round((s.position ?? 0) * 100)}%`)
      .join(', ');
    angle = gradient.angle ?? 90;
  } else {
    // Legacy format: colors[] + locations[]
    // Normalize: editor path-setter may create {0:"#...",1:"#..."} instead of array
    const colors = gradient.colors
      ? (Array.isArray(gradient.colors) ? gradient.colors : Object.values(gradient.colors) as string[])
      : [];
    if (colors.length === 0) return undefined;

    const locations = Array.isArray(gradient.locations)
      ? gradient.locations
      : gradient.locations ? Object.values(gradient.locations) as number[] : undefined;

    const heightPercent = gradient.heightPercentage ?? 50;

    if (colors.length === 1) {
      const color = colors[0];
      colorStops = `${color} 0%, ${hexToRgba(color, 0)} ${heightPercent}%`;
      angle = 0; // single color: bottom-to-top fade
    } else {
      colorStops = colors.map((color, i) => {
        const location = locations?.[i] ?? (i / (colors.length - 1));
        return `${color} ${Math.round(location * 100)}%`;
      }).join(', ');
      angle = gradient.angle ?? 0;
    }
  }

  if (!colorStops) return undefined;

  const styles: CSSProperties = {
    backgroundImage: `linear-gradient(${angle}deg, ${colorStops})`,
  };

  // Background size from `size` field (percentage)
  if (gradient.size !== undefined && gradient.size !== 100) {
    styles.backgroundSize = `${gradient.size}%`;
  }

  return styles;
}

/**
 * Maps a FeaturedMedia gradient overlay to CSS.
 *
 * Media gradients use `heightPercentage` as the overlay element height relative
 * to the media wrapper. Color stops are then rendered inside that cropped
 * bottom overlay, so 25 means the fade covers the bottom 25% of the media.
 */
export function mapMediaGradientOverlayToCSS(gradient: GradientOverlay | undefined): CSSProperties | undefined {
  if (!gradient) return undefined;

  let colorStops: string | undefined;
  let angle: number;

  const stops = gradient.stops && Array.isArray(gradient.stops) && gradient.stops.length > 0
    ? gradient.stops
    : undefined;

  if (stops) {
    const sorted = [...stops].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    colorStops = sorted
      .map(s => `${s.color} ${Math.round((s.position ?? 0) * 100)}%`)
      .join(', ');
    angle = gradient.angle ?? 90;
  } else {
    const colors = gradient.colors
      ? (Array.isArray(gradient.colors) ? gradient.colors : Object.values(gradient.colors) as string[])
      : [];
    if (colors.length === 0) return undefined;

    const locations = Array.isArray(gradient.locations)
      ? gradient.locations
      : gradient.locations ? Object.values(gradient.locations) as number[] : undefined;

    if (colors.length === 1) {
      const color = colors[0];
      colorStops = `${color} 0%, ${hexToRgba(color, 0)} 100%`;
      angle = 0;
    } else {
      colorStops = colors.map((color, i) => {
        const location = locations?.[i] ?? (i / (colors.length - 1));
        return `${color} ${Math.round(location * 100)}%`;
      }).join(', ');
      angle = gradient.angle ?? 0;
    }
  }

  if (!colorStops) return undefined;

  const rawHeight = typeof gradient.heightPercentage === 'number' ? gradient.heightPercentage : 50;
  const heightPercent = Math.max(0, Math.min(100, rawHeight));

  const styles: CSSProperties = {
    backgroundImage: `linear-gradient(${angle}deg, ${colorStops})`,
    top: 'auto',
    bottom: 0,
    height: `${heightPercent}%`,
  };

  if (gradient.size !== undefined && gradient.size !== 100) {
    styles.backgroundSize = `${gradient.size}%`;
  }

  return styles;
}

// ============= Paywall Style Mappers =============

/**
 * Maps ProductCatalogStyle to CSS properties.
 *
 * Handles: backgroundColor, borders (BorderStyle), insets (EdgeInsets), itemsSpacing → gap.
 * V2 replacement for mapSubscriptionListBlockStyles.
 *
 * @param style - ProductCatalogStyle from v2 schema (optional)
 * @returns CSSProperties for inline styling
 */
export function mapProductCatalogStyles(style?: ProductCatalogStyle): CSSProperties {
  if (!style) {
    return {};
  }

  const styles: Record<string, unknown> = {};

  if (style.backgroundColor !== undefined) {
    styles.backgroundColor = style.backgroundColor;
  }

  // Border from BorderStyle object
  if (style.borders) {
    if (style.borders.width != null) {
      styles.border = `${style.borders.width}px solid ${style.borders.color || 'transparent'}`;
    }
    Object.assign(styles, mapCornerRadiusToCSS(style.borders.radius));
  }

  // Padding from EdgeInsets
  Object.assign(styles, mapEdgeInsetsToCSS(style.insets));

  // Item spacing as gap
  if (style.itemsSpacing !== undefined) {
    styles.gap = style.itemsSpacing;
  }

  return styles as CSSProperties;
}

/**
 * Return type for product offer grid styles.
 */
export interface ProductOfferGridStyles {
  gridTemplateColumns: string;
  columnGap: number;
  rowGap?: number;
}

/**
 * Floor for the product-name column, in px (WP8 UX S-2).
 *
 * The percentage a paywall is authored with is measured against a wide frame. At 320 the
 * flagship's 29 % resolved to 52 px, and the plan name broke mid-word — "Monthl / y",
 * "(Annual / )" — on the one screen that asks for money. 72 px is the column width 390
 * already renders, where the same rows read cleanly; below that the percentage yields.
 */
export const PRODUCT_NAME_COLUMN_MIN_WIDTH_PX = 72;

/**
 * Share of the labels grid the floor is never allowed to exceed, so a genuinely narrow
 * container degrades instead of pushing the price column out of the row.
 */
const PRODUCT_NAME_COLUMN_MAX_SHARE = '45%';

/**
 * Calculates CSS Grid styles for product offer item labels.
 *
 * V2 replacement for getLabelsGridStyles.
 * Uses startColumnWidthPercentage instead of leftLabelColumnWidthPercentage.
 *
 * @param hasStartContent - Whether start (left in LTR) column has content
 * @param hasEndContent - Whether end (right in LTR) column has content
 * @param offerStyle - ProductOfferStyle from v2 schema (optional)
 * @returns Grid style properties for labels container
 */
export function getProductOfferGridStyles(
  hasStartContent: boolean,
  hasEndContent: boolean,
  offerStyle?: ProductOfferStyle
): ProductOfferGridStyles {
  const startColumnWidth = offerStyle?.startColumnWidthPercentage || 50;
  const isOneColumnEnd = !hasStartContent;
  const isOneColumnStart = !hasEndContent;

  const columnGap = (isOneColumnStart || isOneColumnEnd)
    ? 0
    : offerStyle?.columnHorizontalPadding || 0;

  let gridTemplateColumns: string;
  if (isOneColumnEnd) {
    gridTemplateColumns = '0% 1fr';
  } else if (isOneColumnStart) {
    gridTemplateColumns = '1fr 0%';
  } else {
    // Both columns carry labels: the authored percentage governs, with a floor so a narrow
    // viewport cannot crush the product name (WP8 UX S-2). `minmax()` resolves to the floor
    // only where the percentage is smaller, so every wider layout is byte-identical to
    // what it rendered before.
    gridTemplateColumns =
      `minmax(min(${PRODUCT_NAME_COLUMN_MIN_WIDTH_PX}px, ${PRODUCT_NAME_COLUMN_MAX_SHARE}), ` +
      `calc(${startColumnWidth}% - ${columnGap / 2}px)) 1fr`;
  }

  return {
    gridTemplateColumns,
    columnGap,
    rowGap: offerStyle?.columnVerticalPadding,
  };
}

/**
 * Maps PaywallFooterStyle to CSS properties.
 *
 * Handles: backgroundColor, opacity, insets (EdgeInsets).
 * V2 replacement for mapPaywallFooterBlockStyles.
 * Note: elementsOrder is handled by component logic, not CSS.
 *
 * @param style - PaywallFooterStyle from v2 schema (optional)
 * @returns CSSProperties for inline styling
 */
export function mapPaywallFooterStyles(style?: PaywallFooterStyle): CSSProperties {
  if (!style) {
    return {};
  }

  return filterUndefined({
    backgroundColor: style.backgroundColor,
    opacity: style.opacity,
    ...mapEdgeInsetsToCSS(style.insets),
  }) as CSSProperties;
}
